import React from 'react';

import './SelectAllToggle.scss';

/**
 * `SelectAllToggle` — the canonical "Žymėti visus" toolbar control.
 *
 * Purely visual: it owns no state and knows nothing about the items being
 * toggled. The hosting page keeps its own selection state and `onToggle`
 * handler — this component just renders the chrome (button + embedded
 * checkbox) so every page on the platform looks the same.
 *
 * Uses BEM-scoped class names (`.select-all-toggle*`) instead of the legacy
 * `.select-all-dropdown` / `.select-all-btn` classes. The legacy classes had
 * been redefined in 8 page-level .scss files with subtle drift (some had a
 * fake dropdown caret, some didn't, some pages had no rules at all) — moving
 * to scoped names and a single SCSS file kills the drift outright and stops
 * page-level CSS from accidentally overriding the component.
 */
interface SelectAllToggleProps {
  /** Number of currently-selected items in the list this toggle drives. */
  selectedCount: number;
  /** Total number of items the toggle would affect when activated. */
  totalCount: number;
  /** Fired when the user clicks the toggle (button OR embedded checkbox). */
  onToggle: () => void;
  /** Visible label. Defaults to "Žymėti visus". */
  label?: string;
  /** Disable the toggle (and its checkbox). */
  disabled?: boolean;
  /** Optional native tooltip. */
  title?: string;
  /** Optional class passthrough on the outer wrapper. */
  className?: string;
}

const SelectAllToggle: React.FC<SelectAllToggleProps> = ({
  selectedCount,
  totalCount,
  onToggle,
  label = 'Žymėti visus',
  disabled = false,
  title,
  className,
}) => {
  const checked = totalCount > 0 && selectedCount === totalCount;
  const wrapperClass = className
    ? `select-all-toggle ${className}`
    : 'select-all-toggle';

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        className="select-all-toggle__btn"
        onClick={onToggle}
        disabled={disabled}
        title={title}
      >
        <input
          type="checkbox"
          className="select-all-toggle__checkbox"
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          aria-label={label}
        />
        <span className="select-all-toggle__label">{label}</span>
      </button>
    </div>
  );
};

export default SelectAllToggle;
